import type { ParseKeys } from "i18next";

/**
 * User-facing copy for the shared error DIALOG (operation failures). Field-level INLINE copy lives in the per-feature
 * `*-errors.ts` helpers — this is intentionally more generic (it fronts a modal, not a field). One entry per server
 * error `code`; unmapped codes fall back to DEFAULT, a missing response to NETWORK.
 *
 * The entries hold message **keys**, never words (ADR-048 Phase 4 slice 6). This module is imported by a pure
 * resolver with no hook of its own, so a string baked in here would have been English forever — and the `satisfies`
 * below means a consumer that forgets to translate one is a typecheck error, not a page that silently reads English.
 */
/**
 * A catalogue key, as i18next's own typed-key union. `string` would compile and then fail at the `t()` call site —
 * which is how `$t(category.Raw)`, a glossary path that does not exist, reached the resolver.
 */
type MessageKey = ParseKeys;

export interface ErrorCopy {
  titleKey: MessageKey;
  messageKey: MessageKey;
}

export const ERROR_MESSAGES = {
  // Facility
  FACILITY_FORBIDDEN: { titleKey: "errors.notAllowed", messageKey: "errors.facilityForbidden" },
  FACILITY_NOT_FOUND: { titleKey: "errors.notFound", messageKey: "errors.facilityNotFound" },
  FACILITY_ARCHIVED: { titleKey: "errors.facilityArchivedTitle", messageKey: "errors.facilityArchived" },
  FACILITY_INVALID: { titleKey: "errors.checkYourInput", messageKey: "errors.facilityInvalid" },
  FACILITY_MOVE_CYCLE: { titleKey: "errors.invalidMove", messageKey: "errors.facilityMoveCycle" },
  FACILITY_STATS_STALE: { titleKey: "errors.facilityChanged", messageKey: "errors.facilityStatsStale" },
  FACILITY_NOT_ARCHIVED: { titleKey: "errors.notArchived", messageKey: "errors.facilityNotArchived" },
  FACILITY_ANCESTOR_ARCHIVED: { titleKey: "errors.parentIsArchived", messageKey: "errors.facilityAncestorArchived" },
  // Category icon (ADR-043). The server collapses every containment/state reason into one opaque FILE_NOT_FOUND, so
  // the copy must cover all of them at once without guessing which applied — and without implying the file is gone
  // when it may simply be out of scope or still processing.
  FILE_NOT_FOUND: { titleKey: "errors.fileNotFoundTitle", messageKey: "errors.fileNotFound" },
  FILE_NOT_AN_IMAGE: { titleKey: "errors.notAnImage", messageKey: "errors.fileNotAnImage" },
  // Kiosk
  KIOSK_FORBIDDEN: { titleKey: "errors.notAllowed", messageKey: "errors.kioskForbidden" },
  KIOSK_PROFILE_NOT_FOUND: { titleKey: "errors.notFound", messageKey: "errors.kioskProfileNotFound" },
  KIOSK_PROFILE_STALE: { titleKey: "errors.profileChanged", messageKey: "errors.kioskProfileStale" },
  KIOSK_NAME_TAKEN: { titleKey: "errors.nameAlreadyUsed", messageKey: "errors.kioskNameTaken" },

  SENSORS_FORBIDDEN: { titleKey: "errors.notAllowed", messageKey: "errors.sensorsForbidden" },
  SENSOR_NOT_FOUND: { titleKey: "errors.notFound", messageKey: "errors.sensorNotFound" },
  SENSOR_IN_USE: { titleKey: "errors.sensorInUseTitle", messageKey: "errors.sensorInUse" },

  MANUAL_PLATFORM_REQUIRED: { titleKey: "errors.manualChangedTitle", messageKey: "errors.manualPlatformRequired" },

  // The manual trash (ADR-056)
  MANUAL_STATE_STALE: { titleKey: "errors.manualChangedTitle", messageKey: "errors.manualStateStale" },
  MANUAL_PARENT_TRASHED: { titleKey: "errors.manualCannotRestoreTitle", messageKey: "errors.manualParentTrashed" },
  MANUAL_PARENT_ARCHIVED: { titleKey: "errors.manualCannotRestoreTitle", messageKey: "errors.manualParentArchived" },
  MANUAL_CATEGORY_GONE: { titleKey: "errors.manualCannotRestoreTitle", messageKey: "errors.manualCategoryGone" },

  // Category
  CATEGORY_NAME_TAKEN: { titleKey: "errors.nameAlreadyUsed", messageKey: "errors.categoryNameTaken" },
  CATEGORY_NOT_FOUND: { titleKey: "errors.notFound", messageKey: "errors.categoryNotFound" },
  CATEGORY_HAS_ATTACHMENTS: { titleKey: "errors.categoryStillInUse", messageKey: "errors.categoryHasAttachments" },
  FACILITY_CATEGORY_HAS_MANUALS: { titleKey: "errors.categoryHasManualsTitle", messageKey: "errors.facilityCategoryHasManuals" },
} as const satisfies Record<string, ErrorCopy>;

export const DEFAULT_ERROR = { titleKey: "errors.defaultTitle", messageKey: "errors.defaultMessage" } as const satisfies ErrorCopy;
export const NETWORK_ERROR = { titleKey: "errors.networkTitle", messageKey: "errors.networkMessage" } as const satisfies ErrorCopy;
